import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSplits } from "@/lib/useSplits";

const posColor = (p) => p === 1 ? "#FFD700" : p === 2 ? "#C0C0C0" : p === 3 ? "#CD7F32" : "#FFFFFF";

export default function StandingsPage() {
  const splitsCfg = useSplits();
  const SPLIT_TABS = [
    ...splitsCfg.map(s => ({ key: s.key, label: s.name, color: s.color })),
    { key: "eternal", label: "Ewige Tabelle", color: "#FFFFFF" },
  ];
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");      // current by default
  const [activeSplit, setActiveSplit] = useState(splitsCfg[0]?.key || "champions");
  const [drivers, setDrivers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Wenn Splits asynchron geladen werden und activeSplit noch nicht passt, anpassen
  useEffect(() => {
    if (!splitsCfg.length) return;
    if (activeSplit === "eternal") return;
    if (!splitsCfg.some(s => s.key === activeSplit)) {
      Promise.resolve().then(() => setActiveSplit(splitsCfg[0].key));
    }
  }, [splitsCfg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/seasons").then(x => setSeasons(x.data)).catch(() => setSeasons([]));
  }, []);

  // Wenn Split sich ändert -> Saison auf die "aktuelle" des Splits setzen
  // (oder, falls keine aktuell, auf die neueste Saison dieses Splits)
  useEffect(() => {
    if (activeSplit === "eternal") return;
    const inSplit = seasons.filter(s => (s.split || "champions") === activeSplit);
    // Falls die aktuell gewählte Saison schon zu diesem Split gehört, beibehalten
    if (seasonId && inSplit.some(s => s.id === seasonId)) return;
    // setState async ausführen damit React-Compiler-Lint nicht meckert
    Promise.resolve().then(() => {
      if (!inSplit.length) { setSeasonId(""); return; }
      const cur = inSplit.find(s => s.is_current);
      const next = cur || [...inSplit].sort((a, b) => (b.year || 0) - (a.year || 0))[0];
      setSeasonId(next.id);
    });
  }, [activeSplit, seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      if (activeSplit === "eternal") {
        const [d, t] = await Promise.all([
          api.get("/standings/eternal/drivers").then(x => x.data).catch(() => []),
          api.get("/standings/eternal/constructors").then(x => x.data).catch(() => []),
        ]);
        setDrivers(d); setTeams(t);
      } else if (seasonId) {
        const q = `?split=${activeSplit}&season_id=${seasonId}`;
        const [d, t] = await Promise.all([
          api.get(`/standings/drivers${q}`).then(x => x.data).catch(() => []),
          api.get(`/standings/constructors${q}`).then(x => x.data).catch(() => []),
        ]);
        setDrivers(d); setTeams(t);
      }
    })();
  }, [seasonId, activeSplit]);

  const seasonObj = seasons.find(s => s.id === seasonId);
  const seasonName = seasonObj?.name || "—";
  const isClosed = !!seasonObj?.closed && activeSplit !== "eternal";
  const driverChampion = isClosed ? drivers.find(d => d.position === 1) : null;
  const teamChampion = isClosed ? teams.find(t => t.position === 1) : null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="standings-page">
      <div className="h-1 w-12 bg-[#00E5FF] mb-4" />
      <h1 className="font-display font-black italic uppercase tracking-normal text-5xl sm:text-7xl leading-none">Wertung</h1>
      <p className="text-neutral-400 mt-3 max-w-2xl">Fahrer- und Konstrukteurswertung – pro Split und Saison oder als Ewige Tabelle.</p>

      <Tabs value={activeSplit} onValueChange={setActiveSplit} className="mt-10">
        <TabsList className="bg-transparent border border-white/10 p-0 rounded-none flex-wrap h-auto">
          {SPLIT_TABS.map(s => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`standings-tab-${s.key}`}
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-5 py-2 text-xs">
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {activeSplit !== "eternal" && (
          <div className="flex items-center gap-3 mt-6" data-testid="season-selector-wrap">
            <span className="text-xs uppercase tracking-widest text-neutral-400">Saison:</span>
            <select value={seasonId} onChange={e => setSeasonId(e.target.value)} data-testid="season-selector"
              className="bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm focus:border-[#00E5FF] outline-none">
              {seasons
                .filter(s => (s.split || "champions") === activeSplit)
                .map(s => (
                  <option key={s.id} value={s.id}>{s.name}{s.game ? ` · ${s.game}` : ""}{s.is_current ? " · aktuell" : ""}</option>
                ))}
              {seasons.filter(s => (s.split || "champions") === activeSplit).length === 0 && (
                <option value="" disabled>Keine Saisons für diesen Split</option>
              )}
            </select>
            <span className="text-xs text-neutral-500 ml-auto">{seasonName}</span>
          </div>
        )}

        {activeSplit === "eternal" && (
          <div className="mt-6 text-xs uppercase tracking-widest text-neutral-400" data-testid="eternal-info">
            Aggregiert über alle Saisons und beide Splits
          </div>
        )}

        {isClosed && (driverChampion || teamChampion) && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="champion-banner">
            {driverChampion && (
              <ChampionCard
                kind="driver"
                title="Fahrer-Champion"
                subtitle={`${seasonObj.name}${seasonObj.game ? ` · ${seasonObj.game}` : ""}`}
                primary={driverChampion.driver_name}
                meta={driverChampion.team_name}
                metaColor={driverChampion.team_color}
                points={driverChampion.points}
                badge={driverChampion.photo_url}
                fallbackColor={driverChampion.team_color}
                number={driverChampion.number}
                countryCode={driverChampion.country_code}
              />
            )}
            {teamChampion && (
              <ChampionCard
                kind="team"
                title="Konstrukteurs-Champion"
                subtitle={`${seasonObj.name}${seasonObj.game ? ` · ${seasonObj.game}` : ""}`}
                primary={teamChampion.team_name}
                meta={teamChampion.short_name}
                metaColor={teamChampion.color}
                points={teamChampion.points}
                badge={teamChampion.badge_url}
                fallbackColor={teamChampion.color}
              />
            )}
          </div>
        )}

        <Tabs defaultValue="drivers" className="mt-6">
          <TabsList className="bg-transparent border border-white/10 p-0 rounded-none">
            <TabsTrigger value="drivers" data-testid="tab-drivers"
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-6 py-2 text-xs">Fahrer</TabsTrigger>
            <TabsTrigger value="teams" data-testid="tab-teams"
              className="data-[state=active]:bg-[#00E5FF] data-[state=active]:text-black uppercase tracking-widest font-bold rounded-none px-6 py-2 text-xs">Konstrukteure</TabsTrigger>
          </TabsList>

          <TabsContent value="drivers" className="mt-6">
            <DriverStandingsTable rows={drivers} activeSplit={activeSplit} splitsCfg={splitsCfg} />
          </TabsContent>

          <TabsContent value="teams" className="mt-6">
            <ConstructorStandingsTable rows={teams} activeSplit={activeSplit} />
          </TabsContent>
        </Tabs>
      </Tabs>
    </div>
  );
}

function ChampionCard({ kind, title, subtitle, primary, meta, metaColor, points, badge, fallbackColor, number, countryCode }) {
  const accent = "#FFD700"; // Gold für Champions
  return (
    <div
      className="relative border border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-[#0B0F19] to-[#0B0F19] p-5 overflow-hidden"
      style={{ boxShadow: "inset 0 0 0 1px rgba(255,215,0,0.08)" }}
      data-testid={`champion-card-${kind}`}
    >
      {/* dezenter Trophy-Glow im Hintergrund */}
      <div
        aria-hidden
        className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-[0.07] blur-2xl"
        style={{ background: accent }}
      />
      <div className="flex items-start gap-4 relative">
        <div
          className="shrink-0 w-20 h-20 border border-amber-400/30 bg-[#11151E] flex items-center justify-center overflow-hidden"
          style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.4)" }}
        >
          {badge ? (
            <img
              src={badge}
              alt={primary}
              className={kind === "driver" ? "w-full h-full object-cover" : "w-14 h-14 object-contain"}
              draggable={false}
            />
          ) : (
            <div className="w-full h-full" style={{ background: fallbackColor || "#1F2937" }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span aria-hidden style={{ color: accent }} className="text-base leading-none">🏆</span>
            <div className="eyebrow text-amber-300/90">{title}</div>
          </div>
          <div className="font-display font-black italic uppercase tracking-tight text-2xl md:text-3xl leading-tight mt-1 truncate">
            {primary}
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs">
            {countryCode && (
              <img
                src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
                alt={countryCode}
                className="w-5 h-3.5 object-cover border border-white/10"
                draggable={false}
              />
            )}
            {typeof number === "number" && number > 0 && (
              <span className="text-neutral-500">#{number}</span>
            )}
            {meta && (
              <span className="font-bold truncate" style={{ color: metaColor || "#9CA3AF" }}>{meta}</span>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">{subtitle}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display font-black italic text-3xl md:text-4xl leading-none" style={{ color: accent }}>
            {points ?? 0}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">Punkte</div>
        </div>
      </div>
    </div>
  );
}

function FlagBox({ code, label }) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      {code && (
        <img src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`} alt={code}
             className="w-5 h-3.5 object-cover border border-white/10" draggable={false} />
      )}
      <span className="text-[9px] uppercase tracking-widest text-neutral-400">{label}</span>
    </div>
  );
}

const fmt = (n) => (n === null || n === undefined ? "—" : n);
const fmtPts = (n) => {
  if (n === null || n === undefined) return "—";
  return Number.isInteger(n) ? String(n) : n.toString();
};

// --- Sortier-Helfer für Standings-Tabellen ---
function sortRows(rows, key, dir) {
  if (!key) return rows;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = a[key];
    const vb = b[key];
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "string" || typeof vb === "string") return sign * String(va).localeCompare(String(vb));
    return sign * ((va || 0) - (vb || 0));
  });
}

function SortableTh({ sortKey, label, currentKey, currentDir, onSort, className = "", defaultDir = "desc", title }) {
  const active = currentKey === sortKey;
  const handleClick = () => {
    if (active) onSort(sortKey, currentDir === "asc" ? "desc" : "asc");
    else onSort(sortKey, defaultDir);
  };
  return (
    <th
      data-testid={`sort-${sortKey}`}
      className={`${className} cursor-pointer select-none transition-colors ${active ? "text-[#00E5FF]" : "hover:text-white"}`}
      title={title || `Sortieren nach ${label}`}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <span>{label}</span>
        {active && (
          <span className="text-[#00E5FF] text-[9px] leading-none">{currentDir === "asc" ? "▲" : "▼"}</span>
        )}
      </span>
    </th>
  );
}

function DriverStandingsTable({ rows, activeSplit, splitsCfg }) {
  const isEternal = activeSplit === "eternal";
  const raceCols = !isEternal && rows[0]?.race_points ? rows[0].race_points : [];
  const totalCols = 9 + raceCols.length + 13 + (isEternal ? 1 : 0);
  const [sortKey, setSortKey] = useState("position");
  const [sortDir, setSortDir] = useState("asc");
  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const onSort = (k, d) => { setSortKey(k); setSortDir(d); };
  const thProps = { currentKey: sortKey, currentDir: sortDir, onSort };
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="text-xs min-w-max" data-testid="driver-standings-table">
        <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
          <tr>
            <SortableTh {...thProps} sortKey="position" label="Pos" defaultDir="asc" className="text-left px-3 py-3 w-12 sticky left-0 bg-[#11151E] z-10" />
            <SortableTh {...thProps} sortKey="driver_name" label="Fahrer" defaultDir="asc" className="text-left px-3 py-3 sticky left-12 bg-[#11151E] z-10 min-w-[180px]" />
            {isEternal && <SortableTh {...thProps} sortKey="split" label="Split" defaultDir="asc" className="text-left px-3 py-3" />}
            <SortableTh {...thProps} sortKey="team_name" label="Team" defaultDir="asc" className="text-left px-3 py-3 min-w-[160px]" />
            <SortableTh {...thProps} sortKey="points" label="Punkte" defaultDir="desc" className="text-right px-3 py-3 w-20" />
            <SortableTh {...thProps} sortKey="interval" label="INT" defaultDir="asc" className="text-right px-3 py-3 w-14" />
            <SortableTh {...thProps} sortKey="gap" label="GAP" defaultDir="asc" className="text-right px-3 py-3 w-14" />
            {raceCols.map((c, i) => (
              <th key={c.event_id} className="px-2 py-2 text-center w-12" title={c.track}>
                <FlagBox code={c.country_code} label={c.short_code || `R${i+1}`} />
              </th>
            ))}
            <SortableTh {...thProps} sortKey="wins" label="P1" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="p2" label="P2" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="p3" label="P3" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="podiums" label="PD" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Podien (P1+P2+P3)" />
            <SortableTh {...thProps} sortKey="top5" label="Top 5" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="top10" label="Top 10" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="best_pos" label="Best P" defaultDir="asc" className="text-right px-2 py-3 w-16" />
            <SortableTh {...thProps} sortKey="best_q" label="Best Q" defaultDir="asc" className="text-right px-2 py-3 w-16" />
            <SortableTh {...thProps} sortKey="poles" label="PP" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Pole Positions" />
            <SortableTh {...thProps} sortKey="fastest_laps" label="FL" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="dotd" label="DOTD" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="penalty_points" label="STR" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Strafpunkte" />
            <SortableTh {...thProps} sortKey="dnfs" label="DNF" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="races" label="Rennen" defaultDir="desc" className="text-right px-2 py-3 w-16" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.driver_id} className="border-t border-white/5 hover:bg-white/[0.03]">
              <td className="px-3 py-2.5 font-display font-black italic text-lg sticky left-0 bg-[#0B0F19] z-10" style={{ color: posColor(row.position) }}>{row.position}</td>
              <td className="px-3 py-2.5 font-bold sticky left-12 bg-[#0B0F19] z-10 whitespace-nowrap">
                <span className="inline-flex items-center gap-2">
                  {row.country_code && (
                    <img src={`https://flagcdn.com/w40/${row.country_code.toLowerCase()}.png`} alt={row.country_code} className="w-5 h-3.5 object-cover border border-white/10 shrink-0" draggable={false} />
                  )}
                  <span>{row.driver_name}</span>
                  <span className="text-neutral-500 ml-1 text-[10px]">#{row.number}</span>
                </span>
              </td>
              {isEternal && (
                <td className="px-3 py-2.5 text-[10px] uppercase tracking-widest" style={{ color: (splitsCfg.find(x => x.key === row.split)?.color) || "#00E5FF" }}>
                  {splitsCfg.find(x => x.key === row.split)?.name || row.split}
                </td>
              )}
              <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: row.team_color }}>
                <span className="inline-flex items-center gap-2">
                  {row.team_badge && <img src={row.team_badge} alt="" className="w-5 h-5 object-contain" />}
                  <span>{row.team_name}</span>
                </span>
              </td>
              <td className="px-3 py-2.5 text-right font-display font-black text-lg text-[#00E5FF]">{fmtPts(row.points)}</td>
              <td className="px-3 py-2.5 text-right text-neutral-400">{row.position === 1 ? "—" : `-${fmtPts(row.interval)}`}</td>
              <td className="px-3 py-2.5 text-right text-neutral-400">{row.position === 1 ? "—" : `-${fmtPts(row.gap)}`}</td>
              {(!isEternal ? (row.race_points || []) : []).map((rc) => (
                <td key={rc.event_id} className="px-2 py-2.5 text-center text-xs font-display font-black">
                  {rc.points > 0 ? <span className="text-white">{fmtPts(rc.points)}</span> : <span className="text-neutral-600">·</span>}
                </td>
              ))}
              <td className="px-2 py-2.5 text-right">{row.wins || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.p2 || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.p3 || 0}</td>
              <td className="px-2 py-2.5 text-right font-semibold text-white">{row.podiums || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.top5 || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.top10 || 0}</td>
              <td className="px-2 py-2.5 text-right">{fmt(row.best_pos)}</td>
              <td className="px-2 py-2.5 text-right">{fmt(row.best_q)}</td>
              <td className="px-2 py-2.5 text-right font-semibold text-white">{row.poles || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.fastest_laps || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.dotd || 0}</td>
              <td className="px-2 py-2.5 text-right" style={{ color: row.penalty_points > 0 ? "#FF3B30" : undefined }}>{row.penalty_points || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.dnfs || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.races || 0}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={totalCols} className="px-4 py-10 text-center text-neutral-500">Keine Daten für diese Auswahl.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ConstructorStandingsTable({ rows, activeSplit }) {
  const isEternal = activeSplit === "eternal";
  const raceCols = !isEternal && rows[0]?.race_points ? rows[0].race_points : [];
  const totalCols = 7 + raceCols.length + 13;
  const [sortKey, setSortKey] = useState("position");
  const [sortDir, setSortDir] = useState("asc");
  const sortedRows = useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);
  const onSort = (k, d) => { setSortKey(k); setSortDir(d); };
  const thProps = { currentKey: sortKey, currentDir: sortDir, onSort };
  return (
    <div className="border border-white/10 overflow-x-auto">
      <table className="text-xs min-w-max" data-testid="team-standings-table">
        <thead className="bg-white/5 text-neutral-400 uppercase tracking-widest">
          <tr>
            <SortableTh {...thProps} sortKey="position" label="Pos" defaultDir="asc" className="text-left px-3 py-3 w-12 sticky left-0 bg-[#11151E] z-10" />
            <SortableTh {...thProps} sortKey="team_name" label="Team" defaultDir="asc" className="text-left px-3 py-3 sticky left-12 bg-[#11151E] z-10 min-w-[220px]" />
            <SortableTh {...thProps} sortKey="points" label="Punkte" defaultDir="desc" className="text-right px-3 py-3 w-20" />
            <SortableTh {...thProps} sortKey="interval" label="INT" defaultDir="asc" className="text-right px-3 py-3 w-14" />
            <SortableTh {...thProps} sortKey="gap" label="GAP" defaultDir="asc" className="text-right px-3 py-3 w-14" />
            {raceCols.map((c, i) => (
              <th key={c.event_id} className="px-2 py-2 text-center w-12" title={c.track}>
                <FlagBox code={c.country_code} label={c.short_code || `R${i+1}`} />
              </th>
            ))}
            <SortableTh {...thProps} sortKey="wins" label="P1" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="p2" label="P2" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="p3" label="P3" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="podiums" label="PD" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Podien (P1+P2+P3)" />
            <SortableTh {...thProps} sortKey="top5" label="Top 5" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="top10" label="Top 10" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="best_pos" label="Best P" defaultDir="asc" className="text-right px-2 py-3 w-16" />
            <SortableTh {...thProps} sortKey="best_q" label="Best Q" defaultDir="asc" className="text-right px-2 py-3 w-16" />
            <SortableTh {...thProps} sortKey="poles" label="PP" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Pole Positions" />
            <SortableTh {...thProps} sortKey="fastest_laps" label="FL" defaultDir="desc" className="text-right px-2 py-3 w-12" />
            <SortableTh {...thProps} sortKey="dotd" label="DOTD" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="penalty_points" label="STR" defaultDir="desc" className="text-right px-2 py-3 w-12" title="Strafpunkte" />
            <SortableTh {...thProps} sortKey="dnfs" label="DNF" defaultDir="desc" className="text-right px-2 py-3 w-14" />
            <SortableTh {...thProps} sortKey="races" label="Rennen" defaultDir="desc" className="text-right px-2 py-3 w-16" />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.team_id} className="border-t border-white/5 hover:bg-white/[0.03]">
              <td className="px-3 py-2.5 font-display font-black italic text-lg sticky left-0 bg-[#0B0F19] z-10" style={{ color: posColor(row.position) }}>{row.position}</td>
              <td className="px-3 py-2.5 font-bold sticky left-12 bg-[#0B0F19] z-10 whitespace-nowrap">
                <span className="inline-flex items-center gap-2">
                  {row.badge_url ? (
                    <img src={row.badge_url} alt={row.team_name} className="w-6 h-6 object-contain border border-white/10 bg-[#0B0F19]" />
                  ) : (
                    <span className="inline-block w-2 h-6" style={{ background: row.color }} />
                  )}
                  <span style={{ color: row.color }}>{row.team_name}</span>
                  {row.short_name && <span className="text-neutral-500 text-[10px]">{row.short_name}</span>}
                </span>
              </td>
              <td className="px-3 py-2.5 text-right font-display font-black text-lg text-[#00E5FF]">{fmtPts(row.points)}</td>
              <td className="px-3 py-2.5 text-right text-neutral-400">{row.position === 1 ? "—" : `-${fmtPts(row.interval)}`}</td>
              <td className="px-3 py-2.5 text-right text-neutral-400">{row.position === 1 ? "—" : `-${fmtPts(row.gap)}`}</td>
              {(!isEternal ? (row.race_points || []) : []).map((rc) => (
                <td key={rc.event_id} className="px-2 py-2.5 text-center text-xs font-display font-black">
                  {rc.points > 0 ? <span className="text-white">{fmtPts(rc.points)}</span> : <span className="text-neutral-600">·</span>}
                </td>
              ))}
              <td className="px-2 py-2.5 text-right">{row.wins || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.p2 || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.p3 || 0}</td>
              <td className="px-2 py-2.5 text-right font-semibold text-white">{row.podiums || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.top5 || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.top10 || 0}</td>
              <td className="px-2 py-2.5 text-right">{fmt(row.best_pos)}</td>
              <td className="px-2 py-2.5 text-right">{fmt(row.best_q)}</td>
              <td className="px-2 py-2.5 text-right font-semibold text-white">{row.poles || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.fastest_laps || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.dotd || 0}</td>
              <td className="px-2 py-2.5 text-right" style={{ color: row.penalty_points > 0 ? "#FF3B30" : undefined }}>{row.penalty_points || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.dnfs || 0}</td>
              <td className="px-2 py-2.5 text-right">{row.races || 0}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={totalCols} className="px-4 py-10 text-center text-neutral-500">Keine Daten.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
